// Circle shape strategy

import { registerShape } from '../services/ShapeRegistry';
import type {
  ShapeDefinition,
  ShapeDrawSettings,
  EdgeData,
  Point,
  CollisionShape,
} from '../types/ShapeTypes';

function drawPath(settings: ShapeDrawSettings): void {
  const { ctx, node, center: { x, y }, zoomLevel, border } = settings;
  const computedBorder = node.border ? node.borderWidth : 0;
  const radius = border
    ? node.width / 2 + computedBorder / 2
    : node.width / 2 + computedBorder;
  ctx.arc(x, y, radius * zoomLevel, 0, Math.PI * 2, false);
}

function getEdgeConnectionPoint(edge: EdgeData, connected: 'from' | 'to'): Point {
  const point: Point = { x: 0, y: 0 };

  const nodeHeight = connected === 'from'
    ? edge.fromNodeHeight + edge.fromNodeBorderWidth * 2
    : edge.toNodeHeight + edge.toNodeBorderWidth * 2;

  const edgeAngle = connected === 'from'
    ? Math.atan2(edge.endPosY - edge.startPosY, edge.endPosX - edge.startPosX)
    : Math.atan2(edge.startPosY - edge.endPosY, edge.startPosX - edge.endPosX);

  const distanceX = Math.cos(edgeAngle) * nodeHeight / 2;
  const distanceY = Math.sin(edgeAngle) * nodeHeight / 2;

  const baseX = connected === 'from' ? edge.startPosX : edge.endPosX;
  const baseY = connected === 'from' ? edge.startPosY : edge.endPosY;

  point.x = baseX + distanceX;
  point.y = baseY + distanceY;

  return point;
}

function getCollisionData(edge: EdgeData, side: 'from' | 'to'): CollisionShape {
  const isFrom = side === 'from';
  const cx = isFrom ? edge.startPosX : edge.endPosX;
  const cy = isFrom ? edge.startPosY : edge.endPosY;
  const width = isFrom ? edge.fromNodeWidth : edge.toNodeWidth;
  const borderValue = isFrom ? edge.fromNodeBorderWidthValue : edge.toNodeBorderWidthValue;
  const radius = width / 2 + borderValue;

  return { type: 'circle', data: { cx, cy, radius } };
}

const circleShape: ShapeDefinition = {
  value: 'circle',
  drawPath,
  getEdgeConnectionPoint,
  getCollisionData,
  resize: { constrainDimensions: true },
};

registerShape(circleShape);

export { circleShape };
